/**
 * Central registry of all TanStack Query cache keys.
 *
 * Every queryKey used across the app must be defined here. This prevents
 * typo-silent mismatches (a wrong key matches nothing without any error),
 * makes cache dependencies auditable from a single place, and ensures that
 * renaming or restructuring a key only requires a change in this file.
 *
 * Usage:
 *   import { queryKeys } from '@/lib/queryKeys';
 *
 *   // In a query
 *   useQuery({ queryKey: queryKeys.user.profile(), queryFn: ... })
 *
 *   // In a cache mutation or invalidation
 *   queryClient.setQueryData(queryKeys.mails.list(listId), newData)
 *   queryClient.invalidateQueries({ queryKey: queryKeys.featureFlag.all() })
 *
 * Key design rules:
 *  - Every factory returns a `readonly [...]` tuple — satisfies the QueryKey
 *    constraint without losing the literal type (no `as QueryKey` cast needed).
 *  - Keys are hierarchical: `all()` / `usersAll()` variants return the common
 *    prefix so that broad invalidation covers all sub-entries automatically.
 *  - `user.profile()` and `user.all()` intentionally return the same key.
 *    The distinction is semantic (call-site intent), not structural. A future
 *    split only touches this file.
 */

import type { QueryKey } from "@tanstack/react-query";

/**
 * Shared staleTime presets for useQuery / useInfiniteQuery.
 *
 * - realtime  (1s)    : data driven by SDK events; short window avoids stale UI on remount
 * - session   (∞)     : never refetch in background; only updated via setQueryData
 * - stable    (5min)  : slow-changing reference data (profile, navbar, banner)
 */
export const STALE_TIME = {
    realtime: 1_000,
    stable: 5 * 60 * 1_000,
    session: Infinity,
} as const;

export const queryKeys = {
    // ─── Agenda ───────────────────────────────────────────────────────────────
    agendaEvents: {
        all: () => ["agendaEvents"] as const satisfies QueryKey,
        window: (startDate: string, endDate: string) =>
            ["agendaEvents", startDate, endDate] as const satisfies QueryKey,
    },

    // ─── Banner ───────────────────────────────────────────────────────────────
    bannerData: () => ["bannerData"] as const satisfies QueryKey,

    // ─── Custom Views ─────────────────────────────────────────────────────────
    customView: {
        all: () => ["customView"] as const satisfies QueryKey,
        detail: (viewId: string) =>
            ["customView", viewId] as const satisfies QueryKey,
        todos: (viewId: string) =>
            ["customView", viewId, "todos"] as const satisfies QueryKey,
        attachments: (viewId: string) =>
            ["customView", viewId, "attachments"] as const satisfies QueryKey,
        links: (viewId: string) =>
            ["customView", viewId, "links"] as const satisfies QueryKey,
        events: (viewId: string) =>
            ["customView", viewId, "events"] as const satisfies QueryKey,
    },

    // ─── Dashboard ────────────────────────────────────────────────────────────
    dashboardRepartition: () =>
        ["dashboardRepartition"] as const satisfies QueryKey,
    dashboardTodos: () => ["dashboardTodos"] as const satisfies QueryKey,
    dashboardViews: () => ["dashboardViews"] as const satisfies QueryKey,

    // ─── Feature Flags ────────────────────────────────────────────────────────
    featureFlag: {
        all: () => ["featureFlag"] as const satisfies QueryKey,
        detail: (flag: string) =>
            ["featureFlag", flag] as const satisfies QueryKey,
    },

    // ─── Licensing ────────────────────────────────────────────────────────────
    fullLicensesDetails: () =>
        ["fullLicensesDetails"] as const satisfies QueryKey,
    licenseCheck: () => ["licenseCheck"] as const satisfies QueryKey,

    // ─── Mails ────────────────────────────────────────────────────────────────
    mails: {
        all: () => ["mails"] as const satisfies QueryKey,
        list: (listId: string) => ["mails", listId] as const satisfies QueryKey,
    },

    mailThread: {
        all: () => ["mailThread"] as const satisfies QueryKey,
        detail: (internetMessageId: string | undefined) =>
            ["mailThread", internetMessageId] as const satisfies QueryKey,
    },

    // ─── Navigation ───────────────────────────────────────────────────────────
    navbarData: () => ["navbarData"] as const satisfies QueryKey,

    // ─── Organisation ─────────────────────────────────────────────────────────
    organization: {
        all: () => ["organization"] as const satisfies QueryKey,
        users: (teamId?: string | null) =>
            ["organizationUsers", teamId] as const satisfies QueryKey,
        usersAll: () => ["organizationUsers"] as const satisfies QueryKey,
    },

    // ─── Organization AI Model ────────────────────────────────────────────────
    organizationAiModel: () =>
        ["organizationAiModel"] as const satisfies QueryKey,

    // ─── Token Profile ────────────────────────────────────────────────────────
    tokenProfile: () => ["tokenProfile"] as const satisfies QueryKey,

    // ─── User ─────────────────────────────────────────────────────────────────
    user: {
        all: () => ["user"] as const satisfies QueryKey,
        avatar: (email: string) =>
            ["userAvatar", email] as const satisfies QueryKey,
        profile: () => ["user"] as const satisfies QueryKey,
        signature: () =>
            ["user", "custom-signature"] as const satisfies QueryKey,
    },

    // ─── Vistassistant ────────────────────────────────────────────────────────
    languagesAvailable: () =>
        ["languagesAvailable"] as const satisfies QueryKey,
    suggestions: (suggestionType: string) =>
        ["suggestions", suggestionType] as const satisfies QueryKey,
    welcomeMessages: () => ["welcomeMessages"] as const satisfies QueryKey,
    assistant: {
        /** History (step-based contract): the user's past conversations. */
        conversations: () =>
            ["assistant", "conversations"] as const satisfies QueryKey,
        /** A keyword search among them: refreshed with the list. */
        conversationSearch: (keyword: string) =>
            [
                "assistant",
                "conversations",
                "search",
                keyword,
            ] as const satisfies QueryKey,
    },
} as const;
